import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDogs } from '../hooks/useDogs';
import { calculateSizeTag, formatDogAge } from '../utils/dogUtils';
import SignaturePad from './SignaturePad';
import { normalizeText } from '../utils/dataUtils';

const HotelBookingForm = ({
    onSubmit,
    onCancel,
    loading,
    formType = 'booking', // 'booking', 'checkin', 'checkout', 'editVetCheckin'
    editingBooking = null,
    bookings = [],
    initialData = null,
    onDirtyChange = () => {}
}) => {
    const navigate = useNavigate();
    const { dogs, loading: dogsLoading, loadDogs } = useDogs();

    useEffect(() => {
        if (loadDogs) {
            loadDogs({ limit: 1000 });
        }
    }, [loadDogs]);

    // Shared form fields (dates, notes, single-dog fields for non-booking modes)
    const [formData, setFormData] = useState({
        dogId: '',
        bookingId: '',
        checkIn: '',
        checkOut: '',
        notes: '',
        vetWeight: '',
        vetTemperature: '',
        vetNotes: '',
        vetMedication: '',
        ownerSignature: '',
        vetCheckedBy: ''
    });

    const [errors, setErrors] = useState({});
    const [selectedDog, setSelectedDog] = useState(null);
    const [dogSearch, setDogSearch] = useState('');
    const [bookingSearch, setBookingSearch] = useState('');

    // Multi-select state — only used in new booking mode
    const [selectedDogIds, setSelectedDogIds] = useState([]);
    const [vetDataByDog, setVetDataByDog] = useState({}); // { dogId: { vetWeight, vetTemperature, ... } }
    const [expandedVetCards, setExpandedVetCards] = useState({}); // { dogId: bool }

    // Tracks the form's state as of the last mount/prefill, to detect unsaved changes
    const initialSnapshotRef = useRef(null);
    const isDirtyRef = useRef(false);

    // Pre-fill form when editing or with initial data
    useEffect(() => {
        let initialFormData;

        if (editingBooking) {
            const checkInDate = editingBooking.checkIn ? new Date(editingBooking.checkIn).toISOString().split('T')[0] : '';
            const checkOutDate = editingBooking.checkOut ? new Date(editingBooking.checkOut).toISOString().split('T')[0] : '';
            const vetSrc = formType === 'editVetCheckin' ? editingBooking.vetCheckIn : null;

            initialFormData = {
                dogId: editingBooking.dog?._id || '',
                bookingId: editingBooking._id || '',
                checkIn: checkInDate,
                checkOut: checkOutDate,
                notes: editingBooking.notes || '',
                vetWeight: vetSrc?.weight || '',
                vetTemperature: vetSrc?.temperature || '',
                vetNotes: vetSrc?.notes || '',
                vetMedication: vetSrc?.medication || '',
                ownerSignature: vetSrc?.ownerSignature || '',
                // Only the clear button sets this. An empty ownerSignature also
                // happens when the signature simply has not been lazily fetched
                // yet, and erasing on that would destroy a stored signature.
                ownerSignatureCleared: false,
                vetCheckedBy: vetSrc?.checkedBy || ''
            };
            setFormData(initialFormData);
            if (editingBooking.dog) setSelectedDog(editingBooking.dog);
        } else if (initialData) {
            initialFormData = { ...formData, ...initialData };
            setFormData(initialFormData);
        } else {
            initialFormData = formData;
        }

        initialSnapshotRef.current = JSON.stringify({ formData: initialFormData, selectedDogIds: [], vetDataByDog: {} });
    }, [editingBooking, initialData]);

    // Report unsaved-changes state to the parent so it can warn before navigating away
    useEffect(() => {
        if (!initialSnapshotRef.current) return;
        const current = JSON.stringify({ formData, selectedDogIds, vetDataByDog });
        const dirty = current !== initialSnapshotRef.current;
        isDirtyRef.current = dirty;
        onDirtyChange(dirty);
    }, [formData, selectedDogIds, vetDataByDog]);

    // Warn on browser tab close/refresh if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirtyRef.current) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    // Clear the parent's dirty flag once this form instance goes away
    useEffect(() => () => onDirtyChange(false), []);

    useEffect(() => {
        if (formData.dogId && dogs.length > 0) {
            setSelectedDog(dogs.find(d => d._id === formData.dogId) || null);
        }
    }, [formData.dogId, dogs]);

    // ── Helpers ──────────────────────────────────────────────────────────────

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // Toggle a dog in/out of the multi-select list
    const toggleDogSelection = (dogId) => {
        setSelectedDogIds(prev => {
            if (prev.includes(dogId)) {
                setVetDataByDog(pd => { const n = { ...pd }; delete n[dogId]; return n; });
                setExpandedVetCards(pe => { const n = { ...pe }; delete n[dogId]; return n; });
                return prev.filter(id => id !== dogId);
            }
            return [...prev, dogId];
        });
        setErrors(prev => ({ ...prev, dogId: '' }));
    };

    const updateVetField = (dogId, field, value) => {
        setVetDataByDog(prev => ({
            ...prev,
            [dogId]: { ...(prev[dogId] || {}), [field]: value }
        }));
    };

    const toggleVetCard = (dogId) => {
        setExpandedVetCards(prev => ({ ...prev, [dogId]: !prev[dogId] }));
    };

    // ── Validation ────────────────────────────────────────────────────────────

    const validateForm = () => {
        const newErrors = {};

        if (formType === 'editVetCheckin') {
            // Only vet numeric fields
        } else if (formType === 'booking' && !editingBooking) {
            // New multi-dog booking
            if (selectedDogIds.length === 0) newErrors.dogId = 'Selecciona al menos un perro';
            if (!formData.checkIn) newErrors.checkIn = 'La fecha de entrada es requerida';
            if (!formData.checkOut) newErrors.checkOut = 'La fecha de salida es requerida';
            else if (formData.checkIn && formData.checkOut <= formData.checkIn)
                newErrors.checkOut = 'La fecha de salida debe ser posterior a la de entrada';
        } else {
            if (formType !== 'checkout' && !formData.dogId)
                newErrors.dogId = 'Selecciona un perro';
            if (formType === 'checkout' && !formData.bookingId)
                newErrors.bookingId = 'Selecciona una reserva para dar salida';
            if (formType !== 'checkout') {
                if (!formData.checkIn) newErrors.checkIn = 'La fecha de entrada es requerida';
                if (!formData.checkOut) newErrors.checkOut = 'La fecha de salida es requerida';
                else if (formData.checkIn && formData.checkOut <= formData.checkIn)
                    newErrors.checkOut = 'La fecha de salida debe ser posterior a la de entrada';
            }
        }

        if (formData.vetWeight && (isNaN(formData.vetWeight) || formData.vetWeight <= 0))
            newErrors.vetWeight = 'El peso debe ser un número positivo';
        if (formData.vetTemperature && (isNaN(formData.vetTemperature) || formData.vetTemperature < 35 || formData.vetTemperature > 43))
            newErrors.vetTemperature = 'La temperatura debe estar entre 35°C y 43°C';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (formType === 'booking' && !editingBooking) {
            // Multi-dog new booking
            onSubmit({
                dogIds: selectedDogIds,
                checkIn: formData.checkIn,
                checkOut: formData.checkOut,
                notes: formData.notes,
                vetDataByDog
            });
        } else {
            onSubmit({
                ...formData,
                vetWeight: formData.vetWeight ? parseFloat(formData.vetWeight) : undefined,
                vetTemperature: formData.vetTemperature ? parseFloat(formData.vetTemperature) : undefined,
            });
        }
    };

    // ── Available options ─────────────────────────────────────────────────────

    const getAvailableOptions = () => {
        if (formType === 'checkin') {
            // Any dog can walk in without a reservation — dogs with a pending
            // 'booked' reservation are shown first (with their dates), followed
            // by dogs with no reservation. Dogs already checked in are excluded.
            const reservationByDog = new Map();
            const checkedInDogIds = new Set();
            bookings.forEach(b => {
                if (!b.dog?._id) return;
                if (b.status === 'booked' && !reservationByDog.has(b.dog._id)) reservationByDog.set(b.dog._id, b);
                if (b.status === 'checked-in') checkedInDogIds.add(b.dog._id);
            });
            return dogs
                .filter(d => !checkedInDogIds.has(d._id))
                .map(d => {
                    const reservation = reservationByDog.get(d._id);
                    const ownerNames = d.owners?.map(o => o.name).join(', ');
                    return {
                        value: d._id,
                        hasReservation: !!reservation,
                        checkInDate: reservation ? new Date(reservation.checkIn) : null,
                        label: reservation
                            ? `${d.name} - ${ownerNames} (${new Date(reservation.checkIn).toLocaleDateString()} - ${new Date(reservation.checkOut).toLocaleDateString()})`
                            : `${d.name} - ${ownerNames} (sin reserva)`
                    };
                })
                .sort((a, b) => {
                    if (a.hasReservation && b.hasReservation) return a.checkInDate - b.checkInDate;
                    if (a.hasReservation !== b.hasReservation) return a.hasReservation ? -1 : 1;
                    return a.label.localeCompare(b.label);
                });
        } else if (formType === 'checkout') {
            return bookings
                .filter(b => b.status === 'checked-in' && b.dog?._id)
                .map(b => ({
                    value: b._id,
                    label: `${b.dog.name} - ${b.dog.owners?.map(o => o.name).join(', ')} (Room since ${new Date(b.checkIn).toLocaleDateString()})`
                }));
        } else {
            return dogs.map(d => ({ value: d._id, label: `${d.name} - ${d.owners?.map(o => o.name).join(', ')}` }));
        }
    };

    const availableOptions = getAvailableOptions();

    const intakeBookingId = useMemo(() => {
        if (editingBooking) return editingBooking._id;
        if (formType === 'checkin' && formData.dogId) {
            const b = bookings.find(b => (b.dog?._id === formData.dogId || b.dog?.id === formData.dogId) && b.status === 'booked');
            return b?._id || null;
        }
        return null;
    }, [editingBooking, formType, formData.dogId, bookings]);

    const handleShareIntakeForm = () => {
        const url = `${window.location.origin}/hotel/intake/${editingBooking?.intakeToken || intakeBookingId}`;
        const ownerPhone = selectedDog?.owners?.[0]?.phone;
        const dogName = selectedDog?.name || 'tu perro';
        const text = `Hola! Te compartimos el formulario de ingreso de *${dogName}* al hotel. Por favor completalo antes de su llegada:\n${url}`;
        if (ownerPhone) {
            window.open(`https://wa.me/${ownerPhone.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`, '_blank');
        } else if (navigator.share) {
            navigator.share({ title: 'Formulario de ingreso', url, text }).catch(() => {});
        } else {
            navigator.clipboard?.writeText(url);
            alert('Enlace copiado al portapapeles');
        }
    };

    const getTitle = () => {
        switch (formType) {
            case 'booking': return editingBooking ? 'Editar Reserva de Hotel' : 'Nueva Reserva de Hotel';
            case 'checkin': return 'Registrar Entrada de Huésped';
            case 'checkout': return 'Registrar Salida de Huésped';
            case 'editVetCheckin': return '🩺 Editar Salud de Entrada';
            default: return 'Reserva de Hotel';
        }
    };

    const getDescription = () => {
        switch (formType) {
            case 'booking': return editingBooking ? 'Actualizar detalles de la reserva.' : 'Crea una nueva reserva — podés seleccionar varios perros y completar la revisión de salud para cada uno.';
            case 'checkin': return 'Registrar entrada del huésped con evaluación de salud opcional.';
            case 'checkout': return 'Registrar salida del huésped con evaluación de salud final.';
            default: return '';
        }
    };

    // ── Per-dog health card (used in new booking mode) ─────────────────────

    const renderDogHealthCard = (dogId) => {
        const dog = dogs.find(d => d._id === dogId);
        if (!dog) return null;
        const vet = vetDataByDog[dogId] || {};
        const expanded = !!expandedVetCards[dogId];

        return (
            <div key={dogId} className="hb-health-card">
                <button
                    type="button"
                    className="hb-health-card-header"
                    onClick={() => toggleVetCard(dogId)}
                >
                    <div className="dog-picker-avatar hb-health-avatar">
                        {dog.pictureUrl || dog.picture
                            ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                            : <span>{dog.name?.[0]?.toUpperCase()}</span>
                        }
                    </div>
                    <div className="hb-health-card-info">
                        <span className="hb-health-card-name">{dog.name}</span>
                        <span className="hb-health-card-sub">
                            {vet.vetWeight || vet.vetTemperature
                                ? `${vet.vetWeight ? vet.vetWeight + ' kg' : ''}${vet.vetWeight && vet.vetTemperature ? ' · ' : ''}${vet.vetTemperature ? vet.vetTemperature + '°C' : ''}`
                                : 'Revisión de salud opcional'}
                        </span>
                    </div>
                    <span className="hb-health-card-toggle">{expanded ? '▲' : '▼'}</span>
                </button>

                {expanded && (
                    <div className="hb-health-card-body">
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Peso (kg)</label>
                                <input
                                    type="number"
                                    value={vet.vetWeight || ''}
                                    onChange={e => updateVetField(dogId, 'vetWeight', e.target.value)}
                                    step="0.1" min="0.1" max="100"
                                    placeholder="e.g. 25.5"
                                />
                            </div>
                            <div className="form-group">
                                <label>Temperatura (°C)</label>
                                <input
                                    type="number"
                                    value={vet.vetTemperature || ''}
                                    onChange={e => updateVetField(dogId, 'vetTemperature', e.target.value)}
                                    step="0.1" min="35" max="43"
                                    placeholder="e.g. 38.5"
                                />
                            </div>
                            <div className="form-group">
                                <label>Personal</label>
                                <input
                                    type="text"
                                    value={vet.vetCheckedBy || ''}
                                    onChange={e => updateVetField(dogId, 'vetCheckedBy', e.target.value)}
                                    placeholder="Responsable de la revisión"
                                    maxLength="100"
                                />
                            </div>
                        </div>
                        <div className="form-group full-width">
                            <label>Notas de salud</label>
                            <textarea
                                value={vet.vetNotes || ''}
                                onChange={e => updateVetField(dogId, 'vetNotes', e.target.value)}
                                placeholder="Observaciones de salud..."
                                rows="4"
                                maxLength="5000"
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Medicación</label>
                            <textarea
                                value={vet.vetMedication || ''}
                                onChange={e => updateVetField(dogId, 'vetMedication', e.target.value)}
                                placeholder="Medicamentos indicados, dosis y frecuencia..."
                                rows="2"
                                maxLength="500"
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Firma del propietario</label>
                            {vet.ownerSignature ? (
                                <div className="signature-preview">
                                    <img src={vet.ownerSignature} alt="Firma" className="signature-preview-img" />
                                    <button type="button" className="signature-clear-btn" onClick={() => updateVetField(dogId, 'ownerSignature', '')}>
                                        Volver a firmar
                                    </button>
                                </div>
                            ) : (
                                <SignaturePad onChange={(sig) => updateVetField(dogId, 'ownerSignature', sig)} />
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────

    const isNewBooking = formType === 'booking' && !editingBooking;

    return (
        <div className="hotel-booking-form">
            <h3>{getTitle()}</h3>
            <p className="form-description">{getDescription()}</p>

            <form onSubmit={handleSubmit}>
                {formType === 'editVetCheckin' && editingBooking && (
                    <div className="vet-edit-dog-header">
                        <strong>🐕 {editingBooking.dog?.name}</strong>
                        <span>{editingBooking.dog?.owners?.map(o => o.name).join(', ')}</span>
                    </div>
                )}

                <div className="form-grid">
                    {formType !== 'editVetCheckin' && (
                        formType === 'checkout' ? (
                            /* ── Checkout: single booking picker ── */
                            <div className="form-group full-width">
                                <label>Seleccionar huésped para dar salida *</label>
                                <input
                                    type="text"
                                    className="dog-picker-search"
                                    placeholder="Buscar huésped..."
                                    value={bookingSearch}
                                    onChange={e => setBookingSearch(e.target.value)}
                                />
                                <div className="dog-picker-list">
                                    {availableOptions
                                        .filter(opt => normalizeText(opt.label).includes(normalizeText(bookingSearch)))
                                        .map(opt => {
                                            const booking = bookings.find(b => b._id === opt.value);
                                            const [dogName, ...rest] = opt.label.split(' - ');
                                            const isSelected = formData.bookingId === opt.value;
                                            return (
                                                <button
                                                    type="button"
                                                    key={opt.value}
                                                    className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                                    onClick={() => { setFormData(prev => ({ ...prev, bookingId: opt.value })); setErrors(prev => ({ ...prev, bookingId: '' })); }}
                                                >
                                                    <div className="dog-picker-avatar">
                                                        {booking?.dog?.pictureUrl || booking?.dog?.picture
                                                            ? <img src={booking.dog.pictureUrl || booking.dog.picture} alt={dogName} />
                                                            : <span>{dogName?.[0]?.toUpperCase()}</span>
                                                        }
                                                    </div>
                                                    <div className="dog-picker-info">
                                                        <div className="dog-picker-name">{dogName}</div>
                                                        <div className="dog-picker-breed">{rest.join(' - ')}</div>
                                                    </div>
                                                    {isSelected && <span className="dog-picker-check">✓</span>}
                                                </button>
                                            );
                                        })
                                    }
                                    {availableOptions.filter(opt => normalizeText(opt.label).includes(normalizeText(bookingSearch))).length === 0 && (
                                        <div className="dog-picker-empty">No hay huéspedes listos para salida</div>
                                    )}
                                </div>
                                {errors.bookingId && <div className="error">{errors.bookingId}</div>}
                            </div>
                        ) : isNewBooking ? (
                            /* ── New booking: multi-select dog picker ── */
                            <div className="form-group full-width">
                                <label>
                                    Perros *
                                    {selectedDogIds.length > 0 && (
                                        <span className="hb-selected-count">{selectedDogIds.length} seleccionado{selectedDogIds.length > 1 ? 's' : ''}</span>
                                    )}
                                </label>
                                <input
                                    type="text"
                                    className="dog-picker-search"
                                    placeholder="Buscar perro..."
                                    value={dogSearch}
                                    onChange={e => setDogSearch(e.target.value)}
                                />
                                <div className="dog-picker-list">
                                    {dogs
                                        .filter(d => normalizeText(`${d.name} ${d.breed} ${d.owners?.map(o => o.name).join(' ')}`).includes(normalizeText(dogSearch)))
                                        .map(dog => {
                                            const isSelected = selectedDogIds.includes(dog._id);
                                            return (
                                                <button
                                                    type="button"
                                                    key={dog._id}
                                                    className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                                    onClick={() => toggleDogSelection(dog._id)}
                                                >
                                                    <div className="dog-picker-avatar">
                                                        {dog.pictureUrl || dog.picture
                                                            ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                                            : <span>{dog.name?.[0]?.toUpperCase()}</span>
                                                        }
                                                    </div>
                                                    <div className="dog-picker-info">
                                                        <div className="dog-picker-name">{dog.name}</div>
                                                        <div className="dog-picker-breed">{dog.breed}{dog.owners?.length ? ` · ${dog.owners[0].name}` : ''}</div>
                                                    </div>
                                                    <div className={`hb-multi-check${isSelected ? ' hb-multi-check--on' : ''}`}>
                                                        {isSelected ? '✓' : '+'}
                                                    </div>
                                                </button>
                                            );
                                        })
                                    }
                                    {dogs.filter(d => normalizeText(`${d.name} ${d.breed}`).includes(normalizeText(dogSearch))).length === 0 && (
                                        <div className="dog-picker-empty">No se encontraron perros</div>
                                    )}
                                </div>
                                {errors.dogId && <div className="error">{errors.dogId}</div>}
                            </div>
                        ) : (
                            /* ── Checkin / edit existing: single dog picker ── */
                            <div className="form-group full-width">
                                <label>{formType === 'checkin' ? 'Seleccionar perro *' : 'Perro *'}</label>
                                {editingBooking ? (
                                    <div className="dog-picker-list dog-picker-list--disabled">
                                        {selectedDog && (
                                            <div className="dog-picker-card dog-picker-card--selected">
                                                <div className="dog-picker-avatar">
                                                    {selectedDog.pictureUrl || selectedDog.picture
                                                        ? <img src={selectedDog.pictureUrl || selectedDog.picture} alt={selectedDog.name} />
                                                        : <span>{selectedDog.name?.[0]?.toUpperCase()}</span>
                                                    }
                                                </div>
                                                <div className="dog-picker-info">
                                                    <div className="dog-picker-name">{selectedDog.name}</div>
                                                    <div className="dog-picker-breed">{selectedDog.breed}</div>
                                                </div>
                                                <span className="dog-picker-check">✓</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            className="dog-picker-search"
                                            placeholder="Buscar perro..."
                                            value={dogSearch}
                                            onChange={e => setDogSearch(e.target.value)}
                                        />
                                        <div className="dog-picker-list">
                                            {availableOptions
                                                .filter(opt => normalizeText(opt.label).includes(normalizeText(dogSearch)))
                                                .map(opt => {
                                                    const dog = (formType === 'booking' || formType === 'checkin') ? dogs.find(d => d._id === opt.value) : null;
                                                    const [dogName, ...rest] = opt.label.split(' - ');
                                                    const isSelected = formData.dogId === opt.value;
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={opt.value}
                                                            className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                                            onClick={() => { setFormData(prev => ({ ...prev, dogId: opt.value })); setErrors(prev => ({ ...prev, dogId: '' })); }}
                                                        >
                                                            <div className="dog-picker-avatar">
                                                                {dog?.pictureUrl || dog?.picture
                                                                    ? <img src={dog.pictureUrl || dog.picture} alt={dogName} />
                                                                    : <span>{dogName?.[0]?.toUpperCase()}</span>
                                                                }
                                                            </div>
                                                            <div className="dog-picker-info">
                                                                <div className="dog-picker-name">{dogName}</div>
                                                                <div className="dog-picker-breed">{rest.join(' - ')}</div>
                                                            </div>
                                                            {isSelected && <span className="dog-picker-check">✓</span>}
                                                        </button>
                                                    );
                                                })
                                            }
                                            {availableOptions.filter(opt => normalizeText(opt.label).includes(normalizeText(dogSearch))).length === 0 && (
                                                <div className="dog-picker-empty">No se encontraron perros</div>
                                            )}
                                        </div>
                                    </>
                                )}
                                {errors.dogId && <div className="error">{errors.dogId}</div>}
                            </div>
                        )
                    )}

                    {/* Date inputs */}
                    {formType !== 'checkout' && formType !== 'editVetCheckin' && (
                        <>
                            <div className="form-group">
                                <label>Fecha de entrada *</label>
                                <input type="date" name="checkIn" value={formData.checkIn} onChange={handleInputChange} required />
                                {errors.checkIn && <div className="error">{errors.checkIn}</div>}
                            </div>
                            <div className="form-group">
                                <label>Fecha de salida *</label>
                                <input type="date" name="checkOut" value={formData.checkOut} onChange={handleInputChange} min={formData.checkIn || ''} required />
                                {errors.checkOut && <div className="error">{errors.checkOut}</div>}
                            </div>
                        </>
                    )}
                </div>

                {/* Notes */}
                {formType !== 'checkout' && formType !== 'editVetCheckin' && (
                    <div className="form-group full-width">
                        <label>Notas de la reserva</label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            placeholder="Requisitos especiales o notas para la estancia..."
                            rows="3"
                        />
                    </div>
                )}

                {/* Per-dog health cards — new booking mode only */}
                {isNewBooking && selectedDogIds.length > 0 && (
                    <div className="hb-health-section">
                        <div className="hb-health-section-header">
                            <span className="hb-health-section-title">🩺 Revisión de salud al ingreso</span>
                            <span className="hb-health-section-sub">Opcional · Una tarjeta por perro</span>
                        </div>
                        {selectedDogIds.map(dogId => renderDogHealthCard(dogId))}
                    </div>
                )}

                {/* Intake form block — checkin mode only */}
                {formType === 'checkin' && intakeBookingId && (
                    <div className="intake-form-block">
                        <div className="intake-form-block-info">
                            <span className="intake-form-block-icon">📋</span>
                            <div>
                                <div className="intake-form-block-title">Formulario de ingreso</div>
                                <div className="intake-form-block-sub">Alimentación, medicamentos, pertenencias y comportamiento</div>
                            </div>
                        </div>
                        <div className="intake-form-block-actions">
                            <button type="button" className="intake-form-open-btn" onClick={() => navigate(`/hotel/intake/${intakeBookingId}/staff`)}>
                                Abrir formulario
                            </button>
                            <button type="button" className="intake-form-share-btn" onClick={handleShareIntakeForm}>
                                📤 Compartir
                            </button>
                        </div>
                    </div>
                )}

                {/* Vet Health Assessment — checkin / checkout / editVetCheckin */}
                {(formType === 'checkin' || formType === 'checkout' || formType === 'editVetCheckin') && (
                    <div className="vet-section">
                        <h4>🩺 Evaluación de salud {formType === 'checkout' ? '(final)' : '(entrada)'}</h4>
                        <p className="section-description">
                            {formType === 'editVetCheckin'
                                ? 'Actualizar información de salud registrada al ingreso'
                                : `Revisión de salud opcional durante la ${formType === 'checkout' ? 'salida' : 'entrada'}`}
                        </p>

                        <div className="form-grid">
                            <div className="form-group">
                                <label>Peso (kg)</label>
                                <input type="number" name="vetWeight" value={formData.vetWeight} onChange={handleInputChange} step="0.1" min="0.1" max="100" placeholder="e.g. 25.5" />
                                {errors.vetWeight && <div className="error">{errors.vetWeight}</div>}
                            </div>
                            <div className="form-group">
                                <label>Temperatura (°C)</label>
                                <input type="number" name="vetTemperature" value={formData.vetTemperature} onChange={handleInputChange} step="0.1" min="35" max="43" placeholder="e.g. 38.5" />
                                {errors.vetTemperature && <div className="error">{errors.vetTemperature}</div>}
                            </div>
                            <div className="form-group">
                                <label>Personal</label>
                                <input type="text" name="vetCheckedBy" value={formData.vetCheckedBy} onChange={handleInputChange} placeholder="Miembro del personal que realizó la evaluación" maxLength="100" />
                            </div>
                        </div>

                        <div className="form-group full-width">
                            <label>Notas de salud</label>
                            <textarea name="vetNotes" value={formData.vetNotes} onChange={handleInputChange} placeholder={`Observaciones de salud o ${formType === 'checkout' ? 'recomendaciones' : 'preocupaciones médicas'}...`} rows="6" maxLength="5000" />
                        </div>
                        <div className="form-group full-width">
                            <label>Medicación</label>
                            <textarea name="vetMedication" value={formData.vetMedication} onChange={handleInputChange} placeholder="Medicamentos indicados, dosis y frecuencia..." rows="2" maxLength="500" />
                        </div>
                        <div className="form-group full-width">
                            <label>Firma del propietario</label>
                            {formData.ownerSignature ? (
                                <div className="signature-preview">
                                    <img src={formData.ownerSignature} alt="Firma del propietario" className="signature-preview-img" />
                                    <button type="button" className="signature-clear-btn" onClick={() => setFormData(prev => ({ ...prev, ownerSignature: '', ownerSignatureCleared: true }))}>
                                        Volver a firmar
                                    </button>
                                </div>
                            ) : (
                                <SignaturePad onChange={(sig) => setFormData(prev => ({ ...prev, ownerSignature: sig, ownerSignatureCleared: false }))} />
                            )}
                        </div>
                    </div>
                )}

                {/* Single-dog info panel — non-booking modes */}
                {selectedDog && !isNewBooking && formType !== 'checkout' && (
                    <div className="selected-dog-info">
                        <h4>🐕 Información del perro</h4>
                        <div className="dog-info-grid">
                            <p><strong>Nombre:</strong> {selectedDog.name}</p>
                            <p><strong>Raza:</strong> {selectedDog.breed}</p>
                            <p><strong>Edad:</strong> {formatDogAge(selectedDog)}</p>
                            {selectedDog.weight && (() => {
                                const sizeTag = calculateSizeTag(selectedDog.weight);
                                return (
                                    <p>
                                        <strong>Size:</strong>
                                        <span className="size-tag-inline">
                                            <span className="size-tag-badge-small" title={sizeTag.description}>{sizeTag.tag}</span>
                                            <span className="size-tag-label-inline">{sizeTag.label}</span>
                                            <span className="size-tag-weight">({selectedDog.weight}kg)</span>
                                        </span>
                                    </p>
                                );
                            })()}
                            <p><strong>Propietario(s):</strong> {selectedDog.owners?.map(o => o.name).join(', ')}</p>
                        </div>
                    </div>
                )}

                {availableOptions.length === 0 && formType !== 'booking' && (
                    <div className="no-options-warning">
                        <p>⚠️ No hay {formType === 'checkin' ? 'perros disponibles para registrar entrada' : 'huéspedes listos para salida'}.</p>
                        {formType === 'checkout' && <p>Registra una entrada primero.</p>}
                    </div>
                )}

                <div className="form-actions">
                    <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={loading || dogsLoading || (availableOptions.length === 0 && formType !== 'booking' && formType !== 'editVetCheckin')}
                    >
                        {loading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default HotelBookingForm;
